/** Single source of truth for URLs. No route path is ever written inline. */
export const ROUTES = {
  /** `Log_in_1` — the pre-login landing with the two buttons. */
  welcome: '/welcome',
  login: '/login',
  /** The TOTP challenge. Reachable only mid-login — see preAuthStore. */
  loginTwoFactor: '/login/2fa',
  register: '/register',
  /** Target of the confirmation link; expects `?token=`. */
  confirmEmail: '/confirm-email',
  forgotPassword: '/forgot-password',
  /** Target of the reset link; expects `?token=`. */
  resetPassword: '/reset-password',
  home: '/',
  wallet: '/wallet',
  /** Choose how to top up. Crypto is the only live method. */
  deposit: '/deposit',
  /** The deposit address screen. `?network=trc20|bsc` selects the rail. */
  depositCrypto: '/deposit/crypto',
  /** `Top_Up_6` / `Top_Up_7` — one deposit's receipt. */
  depositReceipt: '/deposit/receipt/:id',
  /** `Top_Up_3` / `Top_Up_4` — the user's own id, to hand to a sender. */
  shareId: '/deposit/id',
  /** Choose how to send money out. Mirrors the deposit chooser. */
  withdraw: '/withdraw',
  /** USDT to an external address. `?network=trc20|bsc` selects the rail. */
  withdrawCrypto: '/withdraw/crypto',
  /** Roubles to a bank card. One provider, never shown. */
  withdrawCard: '/withdraw/card',
  /** USDT to another Novix customer. */
  transfer: '/transfer',
  cards: '/cards',
  payouts: '/payouts',
  profile: '/profile',
  /** "Account safety" — the mockup's Profile_2. */
  profileSecurity: '/profile/security',
  profileTwoFactor: '/profile/security/2fa',
  /**
   * The transfer contact book. Under `/profile` rather than `/contacts`, because
   * it is a setting of the account rather than a destination of its own — the
   * tab bar has five slots and none of them is this.
   */
  profileContacts: '/profile/contacts',
  /**
   * The saved-card book. NOT `/cards`, which is the tab-bar route for the
   * (still placeholder) card product — a payout address book and a card product
   * are different things and must not share a URL.
   */
  profileCards: '/profile/cards'
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The complete public surface. Everything not listed here needs a session.
 *
 * Written as a list rather than derived from the router so that adding a screen
 * cannot quietly make it public: a new route is protected by construction, and
 * opening it up means editing this line and being seen to do it.
 */
export const PUBLIC_ROUTES: readonly string[] = [
  ROUTES.welcome,
  ROUTES.login,
  ROUTES.loginTwoFactor,
  ROUTES.register,
  ROUTES.confirmEmail,
  ROUTES.forgotPassword,
  ROUTES.resetPassword
];

/**
 * Validates a "send me back here after login" target.
 *
 * The value travels in router history state, which a page the user was on could
 * in principle influence, so it is treated as untrusted: only a same-origin
 * absolute path is accepted. `//evil.example` is a protocol-relative URL that
 * `<Navigate>` would happily follow off-site, which is why the second test is
 * not redundant.
 */
export function isInternalPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/\\')
  );
}

/** The post-login destination carried in history state, or the home screen. */
export function redirectTargetFrom(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return isInternalPath(from) ? from : ROUTES.home;
}

/** `/deposit/receipt/:id` with the id filled in. Never build this path inline. */
export function depositReceiptPath(id: string): string {
  return `/deposit/receipt/${encodeURIComponent(id)}`;
}
